/**
 * Unified RecipientPicker Types
 * Consolidates all sharing/recipient selection patterns
 */

export interface Recipient {
  id: string;
  type: 'user' | 'group';
  name: string;
  subtitle?: string;
  description?: string;
  source?: string;
}

export interface RecipientSelection {
  users: string[];
  groups: string[];
}

export type RecipientPickerVariant =
  | 'autocomplete'      // Search-based with autocomplete (replaces RecipientAutocomplete)
  | 'side-by-side'      // Users and groups in side-by-side dropdowns (replaces SharingSelector)
  | 'tabs'              // Tab-based UI (replaces SimpleUserGroupSelector)
  | 'dropdown-only'     // Single dropdown for groups only (replaces SimpleRecipientSelector)
  | 'single'            // Single recipient selection (replaces SingleRecipientAutocomplete);

export interface RecipientPickerProps {
  /** Current selection */
  value: RecipientSelection;

  /** Callback when selection changes */
  onChange: (selection: RecipientSelection) => void;

  /** UI variant to use */
  variant?: RecipientPickerVariant;

  /** Label to show above the picker */
  label?: string;

  /** Placeholder text for search/input */
  placeholder?: string;

  /** Whether the picker is disabled */
  disabled?: boolean;

  /** Error state */
  error?: boolean;

  /** Helper text */
  helperText?: string;

  /** Whether to allow multiple selections (false for 'single' variant) */
  multiple?: boolean;

  /** Whether to show users in selection */
  showUsers?: boolean;

  /** Whether to show groups in selection */
  showGroups?: boolean;

  /** Whether to show recently used items */
  showRecent?: boolean;

  /** Maximum number of selections allowed */
  maxSelections?: number;

  /** Search configuration preset (for autocomplete variant) */
  searchConfig?: 'sharing' | 'userAssignment' | 'groupManagement' | 'quickSearch';

  /** Custom filter function for recipients */
  filterRecipients?: (recipient: Recipient) => boolean;
}

/**
 * Legacy prop format for backward compatibility
 * Automatically converted to new format internally
 */
export interface LegacyRecipientProps {
  /** Comma-separated user IDs (deprecated) */
  userIds?: string;

  /** Comma-separated group IDs (deprecated) */
  groupIds?: string;

  /** Callback with comma-separated strings (deprecated) */
  onChange?: (userIds: string, groupIds: string) => void;
}

/**
 * Utility functions for format conversion
 */
export const RecipientUtils = {
  /**
   * Parse comma-separated IDs into array
   */
  parseIds: (ids?: string): string[] => {
    if (!ids) {return [];}
    return ids.split(',').map(id => id.trim()).filter(Boolean);
  },

  /**
   * Convert array of IDs to comma-separated string
   */
  formatIds: (ids: string[]): string => {
    return ids.filter(Boolean).join(', ');
  },

  /**
   * Convert legacy format to new format
   */
  fromLegacy: (userIds?: string, groupIds?: string): RecipientSelection => ({
    users: RecipientUtils.parseIds(userIds),
    groups: RecipientUtils.parseIds(groupIds)
  }),

  /**
   * Convert new format to legacy format
   */
  toLegacy: (selection: RecipientSelection): { userIds: string; groupIds: string } => ({
    userIds: RecipientUtils.formatIds(selection.users),
    groupIds: RecipientUtils.formatIds(selection.groups)
  }),

  /**
   * Check if selection is empty
   */
  isEmpty: (selection: RecipientSelection): boolean => {
    return selection.users.length === 0 && selection.groups.length === 0;
  },

  /**
   * Get total count of selected recipients
   */
  getCount: (selection: RecipientSelection): number => {
    return selection.users.length + selection.groups.length;
  },

  /**
   * Create empty selection
   */
  empty: (): RecipientSelection => ({
    users: [],
    groups: []
  })
};
